import { calcConnTime, fmtConn, formatShift, getAirlineCode } from "./timeUtils";
import { STATUS_EXPORT_KEY } from "./constants";
import { terminalFallback, REMARK_PATTERN } from "./entryUtils";
import { AIRLINE_COLORS } from "../components/shared";
import { AIRPORTS } from "../components/FlightRouteMap";

// ── Map helpers ──────────────────────────────────────────────────────────────
const MAP_W = 900, MAP_H = 240;
// Radial (polar) layout — LAX at center, airports placed by bearing and sqrt-scaled distance.
// This ensures short-haul routes (SFO, PHX, SAN) are as legible as long-haul (NRT, LHR).
const CX = MAP_W / 2;  // 450 — horizontal center
const CY = MAP_H / 2;  // 120 — vertical center
const RX = 390;         // horizontal radius (east-west spread)
const RY = 100;         // vertical radius (north-south spread, compressed for wide canvas)

const [LAX_LAT, LAX_LON] = AIRPORTS.LAX;

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function bearingRad(lat1, lon1, lat2, lon2) {
  const d = Math.PI / 180;
  const dLon = (lon2 - lon1) * d;
  const y = Math.sin(dLon) * Math.cos(lat2 * d);
  const x = Math.cos(lat1 * d) * Math.sin(lat2 * d) -
            Math.sin(lat1 * d) * Math.cos(lat2 * d) * Math.cos(dLon);
  return Math.atan2(y, x); // radians, 0 = north, π/2 = east
}

function arcPath(x1, y1, x2, y2) {
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const bend = Math.min(len * 0.28, 70);
  const cx = mx - (dy / len) * bend;
  const cy = my + (dx / len) * bend;
  return `M${x1.toFixed(1)},${y1.toFixed(1)} Q${cx.toFixed(1)},${cy.toFixed(1)} ${x2.toFixed(1)},${y2.toFixed(1)}`;
}

const ARC_COLOR = "#1a3a9e";

function buildMapSvg(entries) {
  // Collect unique non-LAX airport codes in this shift
  const codes = new Set();
  entries.forEach(e => {
    const o = (e.origin || "").toUpperCase(), d = (e.dest || "").toUpperCase();
    if (o && o !== "LAX" && AIRPORTS[o]) codes.add(o);
    if (d && d !== "LAX" && AIRPORTS[d]) codes.add(d);
  });

  // Distances from LAX — capped at 500 km minimum so nearby airports scale outward
  const distMap = {};
  for (const code of codes) {
    const [lat, lon] = AIRPORTS[code];
    distMap[code] = haversineKm(LAX_LAT, LAX_LON, lat, lon);
  }
  const maxDist = Math.max(...Object.values(distMap), 500);

  // Radial position: correct bearing, blended distance (15% floor + 85% sqrt-scaled).
  // The floor prevents nearby airports from overlapping LAX; sqrt compresses long-haul.
  function codeToXY(code) {
    const [lat, lon] = AIRPORTS[code];
    const dist  = distMap[code] ?? haversineKm(LAX_LAT, LAX_LON, lat, lon);
    const brg   = bearingRad(LAX_LAT, LAX_LON, lat, lon);
    const r     = 0.15 + 0.85 * Math.sqrt(dist / maxDist);
    const angle = brg - Math.PI / 2; // convert N=0 bearing to screen angle where E=0
    return [CX + Math.cos(angle) * r * RX, CY + Math.sin(angle) * r * RY];
  }

  // Route arcs — inbound dashed, outbound solid (same as before)
  let arcs = "";
  entries.forEach(e => {
    const origCode = (e.origin || "").toUpperCase();
    const destCode = (e.dest   || "").toUpperCase();
    if (AIRPORTS[origCode] && origCode !== "LAX") {
      const [ox, oy] = codeToXY(origCode);
      arcs += `<path d="${arcPath(ox, oy, CX, CY)}" stroke="${ARC_COLOR}" stroke-width="1.2" fill="none" opacity="0.45" stroke-dasharray="4 3"/>`;
    }
    if (AIRPORTS[destCode] && destCode !== "LAX") {
      const [tx, ty] = codeToXY(destCode);
      arcs += `<path d="${arcPath(CX, CY, tx, ty)}" stroke="${ARC_COLOR}" stroke-width="1.2" fill="none" opacity="0.65"/>`;
    }
  });

  // Airport dots + labels — collect positions first, then resolve collisions
  const seen = new Set(["LAX"]);
  const labelItems = [];
  entries.forEach(e => {
    for (const code of [(e.origin || "").toUpperCase(), (e.dest || "").toUpperCase()]) {
      if (!code || seen.has(code) || !AIRPORTS[code]) continue;
      seen.add(code);
      const [mx, my] = codeToXY(code);
      const lx = mx + (mx >= CX ? 4 : -4 - code.length * 4.2);
      labelItems.push({ code, mx, my, lx, ly: my + 3 });
    }
  });

  // Iterative Y-axis collision resolution — push overlapping labels apart vertically
  const LABEL_H = 9, CHAR_W = 4.8;
  for (let iter = 0; iter < 60; iter++) {
    let moved = false;
    for (let i = 0; i < labelItems.length; i++) {
      for (let j = i + 1; j < labelItems.length; j++) {
        const a = labelItems[i], b = labelItems[j];
        const ax1 = a.lx, ax2 = a.lx + a.code.length * CHAR_W + 2;
        const bx1 = b.lx, bx2 = b.lx + b.code.length * CHAR_W + 2;
        if (ax1 >= bx2 || ax2 <= bx1) continue; // no horizontal overlap
        const ay1 = a.ly - LABEL_H, ay2 = a.ly + 1;
        const by1 = b.ly - LABEL_H, by2 = b.ly + 1;
        if (ay1 >= by2 || ay2 <= by1) continue; // no vertical overlap
        const push = (Math.min(ay2, by2) - Math.max(ay1, by1)) / 2 + 0.5;
        if (a.ly <= b.ly) { a.ly -= push; b.ly += push; }
        else               { a.ly += push; b.ly -= push; }
        moved = true;
      }
    }
    if (!moved) break;
  }

  let markers = "";
  for (const { code, mx, my, lx, ly } of labelItems) {
    markers += `<circle cx="${mx.toFixed(1)}" cy="${my.toFixed(1)}" r="2.5" fill="#555e6e" opacity="0.7"/>`;
    markers += `<text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" font-size="7" font-family="'Barlow','Helvetica Neue',Arial,sans-serif" fill="#333" letter-spacing="0.04em" stroke="#eef0f5" stroke-width="3" paint-order="stroke fill">${code}</text>`;
  }

  const laxM =
    `<circle cx="${CX}" cy="${CY}" r="7" fill="${ARC_COLOR}" opacity="0.9"/>` +
    `<circle cx="${CX}" cy="${CY}" r="3" fill="#fff"/>` +
    `<text x="${CX.toFixed(1)}" y="${(CY + 20).toFixed(1)}" font-size="9" font-weight="700" font-family="'Barlow','Helvetica Neue',Arial,sans-serif" fill="${ARC_COLOR}" letter-spacing="0.08em" text-anchor="middle" stroke="#eef0f5" stroke-width="3" paint-order="stroke fill">LAX</text>`;

  return `<svg viewBox="0 0 ${MAP_W} ${MAP_H}" xmlns="http://www.w3.org/2000/svg" style="width:100%;display:block;border-radius:6px;border:1px solid #d0d3da;"><rect width="${MAP_W}" height="${MAP_H}" fill="#eef0f5"/>${arcs}${markers}${laxM}</svg>`;
}

function h(str) {
  return String(str || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

// Renders a "[WHO HH:MM] text" remark log as full-width lines — a small muted
// tag followed by the message on the same line — instead of one uppercase
// run-on blob crammed into a narrow table column.
function remarkHtml(remark) {
  const lines = String(remark || "").split("\n").filter(Boolean);
  if (!lines.length) return "";
  return `<div class="remark-block">` + lines.map(line => {
    const match = line.match(REMARK_PATTERN);
    if (!match) return `<div class="remark-line"><span class="remark-text">${h(line)}</span></div>`;
    const [, who, time, text] = match;
    return `<div class="remark-line"><span class="remark-tag">${h(who)} &middot; ${h(time)}</span><span class="remark-text">${h(text)}</span></div>`;
  }).join("") + `</div>`;
}

const LOGO_CODE_MAP = { LHG: "LH", "4Y": "LH", WK: "LH" };

function airlineLogoHtml(iata) {
  if (!iata) return "";
  const code     = iata.toUpperCase().slice(0, iata.toUpperCase() === "LHG" ? 3 : 2);
  const logoCode = LOGO_CODE_MAP[code] || code;
  const bg       = (AIRLINE_COLORS[code] || { bg: "#444" }).bg.replace(/[^a-zA-Z0-9#(),%. ]/g, "");
  const url      = `https://www.gstatic.com/flights/airline_logos/70px/${h(logoCode)}.png`;
  // White background (matches app) — falls back to brand colour if image fails
  return `<span class="al-chip" id="chip-${h(code)}-${Math.random().toString(36).slice(2)}" style="background:#fff">` +
    `<img src="${url}" alt="${h(code)}" class="al-img" onerror="this.parentNode.style.background='${bg}';this.style.display='none'">` +
    `</span>`;
}

function exportCSV(entries, dateLabel, saLogoData, activeSCCO, shiftStr, format = "html") {
  const sorted = [...entries].sort((a, b) => {
    const da = a.atd || a.etd || a.std || "";
    const db = b.atd || b.etd || b.std || "";
    const cmp = da.localeCompare(db);
    if (cmp !== 0) return cmp;
    // Tiebreak on inbound arrival time
    const ta = a.ata || a.eta || a.sta || "";
    const tb = b.ata || b.eta || b.sta || "";
    return ta.localeCompare(tb);
  });

  const activeEntries  = sorted.filter(e => e.section !== "outlook");
  const outlookEntries = sorted.filter(e => e.section === "outlook");
  const sumStats = (arr) => arr.reduce((acc, e) => ({
    pax:     acc.pax     + (Number(e.ttlPax)  || 0),
    exp:     acc.exp     + (Number(e.paxExp)   || 0),
    rbk:     acc.rbk     + (Number(e.paxRbk)  || 0),
    bags:    acc.bags    + (Number(e.ttlBags)  || 0),
    success: acc.success + (e.status === "SUCCESS" || e.status === "REBOOKED" ? 1 : 0),
    total:   acc.total   + 1,
  }), { pax:0, exp:0, rbk:0, bags:0, success:0, total:0 });
  const active  = sumStats(activeEntries);
  const outlook = sumStats(outlookEntries);

  const rows = sorted.map(e => {
    const conn    = calcConnTime(e);
    const inTime  = e.ata ? e.ata + " ATA" : e.eta ? e.eta + " ETA" : e.sta ? e.sta + " STA" : "--";
    const outTime = e.atd ? e.atd + " ATD" : e.etd ? e.etd + " ETD" : e.std ? e.std + " STD" : "--";
    const isOutlook   = e.section === "outlook";
    const rowClass    = isOutlook ? "outlook-row" : "";
    const sectionTag  = isOutlook
      ? `<span class="section-tag">Post-Shift</span>`
      : `<span class="section-tag active">Active</span>`;
    const statusKey = e.cancelled ? "CANCELLED" : (STATUS_EXPORT_KEY[e.status] ?? (e.status || "MONITORING"));
    const statusLabel = e.cancelled ? "CANCELLED" : (e.status || "MONITORING");
    const connClass = conn !== null && conn < 0 ? "conn-time neg" : "conn-time";
    const origin = (e.origin || "").toUpperCase();
    const dest   = (e.dest   || "").toUpperCase();

    const inAirline  = getAirlineCode(e.inAirline, e.inFlt);
    const outAirline = getAirlineCode(e.outAirline, e.outFlt);
    const inFallback  = terminalFallback(inAirline);
    const outFallback = terminalFallback(outAirline);
    const inGateTag  = e.inGate  ? `<span class="gate-tag">${h(e.inGate)}</span>`
                                 : `<span class="gate-tag gate-tag-fallback">${h(inFallback)}</span>`;
    const outGateTag = e.outGate ? `<span class="gate-tag">${h(e.outGate)}</span>`
                                 : `<span class="gate-tag gate-tag-fallback">${h(outFallback)}</span>`;
    return `
      <tr class="${rowClass}">
        <td>
          <div class="flt-row">${airlineLogoHtml(inAirline)}<div><div class="flt-num">${h(e.inFlt) || "--"}${inGateTag}</div><div class="sub">${h(origin)}<span class="arrow"> › </span>${h(inTime)}</div></div></div>
        </td>
        <td>
          <div class="flt-row">${airlineLogoHtml(outAirline)}<div><div class="flt-num flt-out">${h(e.outFlt) || "--"}${outGateTag}</div><div class="sub">${h(dest)}<span class="arrow"> › </span>${h(outTime)}</div></div></div>
        </td>
        <td class="center">${conn !== null ? `<span class="${connClass}">${fmtConn(conn)}</span>` : `<span style="color:var(--border)">—</span>`}</td>
        <td class="center">
          <div class="num">${h(e.ttlPax) || "—"}</div>
          ${e.paxExp ? `<div class="sub-stat green">${h(e.paxExp)} exp</div>` : ""}
          ${e.paxRbk ? `<div class="sub-stat blue">${h(e.paxRbk)} rbk</div>` : ""}
        </td>
        <td class="center">
          <div class="num">${h(e.ttlBags) || "—"}</div>
          ${e.bagsExp ? `<div class="sub-stat green">${h(e.bagsExp)} exp</div>` : ""}
        </td>
        <td><span class="badge badge-${statusKey}"><span class="badge-dot badge-dot-${statusKey}"></span>${h(statusLabel)}</span></td>
        <td class="center">${sectionTag}</td>
      </tr>${e.remark ? `
      <tr class="remark-row">
        <td colspan="7">${remarkHtml(e.remark)}</td>
      </tr>` : ""}`;
  }).join("");

  const now = new Date().toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"});
  const sccoLine = activeSCCO
    ? `${h(activeSCCO.name)}${activeSCCO.badge ? " &nbsp;·&nbsp; #" + h(activeSCCO.badge) : ""}`
    : null;
  const shiftDisplay = shiftStr ? h(formatShift(shiftStr)) : null;

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>LAX Connection Log — ${h(dateLabel)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Barlow:wght@100;200;300;400;500;600;700&display=swap" rel="stylesheet">
<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg:      #ffffff;
  --surface: #ffffff;
  --card:    #f5f5f7;
  --border:  #d0d3da;
  --sub:     #e8e9ed;
  --silver:  #555e6e;
  --light:   #1a1e28;
  --white:   #111318;
  --accent:  #1a3a9e;
  --mono:    'HelveticaNeueStd', 'Barlow', 'Helvetica Neue', Arial, sans-serif;
  --sans:    'HelveticaNeueStd', 'Barlow', 'Helvetica Neue', Arial, sans-serif;
}
body {
  background: var(--bg);
  color: var(--white);
  font-family: var(--sans);
  padding: 40px 44px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* ── Header ─────────────────────────────────────────── */
.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--border);
}
.header-brand { display: flex; align-items: center; gap: 16px; }
.header-brand img { width: 44px; height: 44px; object-fit: contain; }
.brand-title  { font-size: 10px; font-weight: 700; letter-spacing: 0.28em; color: var(--light); text-transform: uppercase; margin-bottom: 3px; }
.brand-sub    { font-size: 9px;  letter-spacing: 0.18em; color: var(--silver); text-transform: uppercase; margin-bottom: 2px; }
.brand-carrier{ font-size: 9px;  letter-spacing: 0.14em; color: var(--accent); text-transform: uppercase; font-weight: 600; }
.header-right { text-align: right; }
.header-date  { font-family: var(--mono); font-size: 20px; font-weight: 700; color: var(--light); letter-spacing: 0.04em; }
.header-meta  { margin-top: 6px; display: flex; flex-direction: column; align-items: flex-end; gap: 3px; }
.header-meta-row { font-size: 9px; color: var(--silver); letter-spacing: 0.1em; text-transform: uppercase; }
.header-meta-row span { color: var(--light); font-weight: 600; }

/* ── Stats bar ────────────────────────────────────────── */
.stats-bar {
  display: grid; grid-template-columns: 90px repeat(6, 1fr);
  gap: 1px; background: var(--border);
  border: 1px solid var(--border); border-radius: 8px;
  margin-bottom: 24px; overflow: hidden;
}
.stat-cell { background: var(--card); padding: 12px 6px; text-align: center; }
.stat-cell-label { background: var(--sub); padding: 12px 10px; display: flex; align-items: center; }
.stat-row-label { font-size: 8px; font-weight: 700; letter-spacing: 0.14em; color: var(--silver); text-transform: uppercase; line-height: 1.3; }
.stat-header-cell { background: var(--sub); padding: 8px 6px; text-align: center; display: flex; align-items: flex-end; justify-content: center; }
.stat-val   { font-family: var(--mono); font-size: 22px; font-weight: 700; color: var(--light); line-height: 1; white-space: nowrap; }
.stat-label { font-size: 7px; letter-spacing: 0.1em; color: var(--silver); text-transform: uppercase; margin-top: 4px; white-space: nowrap; }

/* ── Table ────────────────────────────────────────────── */
table { width: 100%; border-collapse: collapse; font-size: 11px; table-layout: auto; }
thead tr { border-bottom: 2px solid var(--border); background: var(--card); }
thead th  { padding: 8px 8px; font-size: 7px; font-weight: 700; letter-spacing: 0.2em;
            color: var(--silver); text-transform: uppercase; text-align: left; white-space: nowrap; }
thead th.center { text-align: center; }
tbody tr  { border-bottom: 1px solid var(--border); }
tbody tr:has(+ .remark-row) { border-bottom: none; }
tbody tr.outlook-row { opacity: 0.82; }
td { padding: 10px 8px; vertical-align: middle; }

/* ── Cell content ─────────────────────────────────────── */
.flt-row  { display: flex; align-items: center; gap: 8px; }
.al-chip  { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 6px; flex-shrink: 0; overflow: hidden; border: 1px solid rgba(255,255,255,0.12); }
.al-img   { width: 85%; height: 85%; object-fit: contain; display: block; }
.flt-num  { font-family: var(--mono); font-size: 14px; font-weight: 700; color: var(--light); letter-spacing: 0.06em; white-space: nowrap; }
.flt-out  { color: #3a4050; }
.sub      { font-size: 9px; color: var(--silver); letter-spacing: 0.06em; margin-top: 4px; text-transform: uppercase; white-space: nowrap; }
.arrow    { color: var(--border); margin: 0 2px; }
.center   { text-align: center; vertical-align: middle; }
.num      { font-family: var(--mono); font-size: 16px; font-weight: 700; color: var(--light); }
.sub-stat { font-size: 9px; font-family: var(--mono); margin-top: 3px; }
.sub-stat.green { color: #2d7a2d; }
.sub-stat.blue  { color: #4444aa; }
.conn-time     { font-family: var(--mono); font-size: 12px; font-weight: 700; color: var(--light); white-space: nowrap; display: inline-block; }
.conn-time.neg { color: #8a6200; }
.gate-tag { font-family: var(--mono); font-size: 9px; font-weight: 700; color: var(--accent);
            background: #eef1fb; border: 1px solid #c0cae8; border-radius: 3px;
            padding: 1px 5px; margin-left: 7px; letter-spacing: 0.08em; vertical-align: middle; white-space: nowrap; }
.gate-tag-fallback { color: #888; background: #f4f4f4; border-color: #ccc; }

/* ── Status badges ────────────────────────────────────── */
.badge { display:inline-flex; align-items:center; gap:5px; padding:3px 8px;
         border:1px solid; border-radius:3px; font-size:8px; font-weight:700;
         letter-spacing:0.12em; text-transform:uppercase; white-space:nowrap; font-family:var(--sans); }
.badge-dot { width:5px; height:5px; border-radius:50%; display:inline-block; flex-shrink:0; }
.badge-SUCCESS      { background:#e8f5e8; border-color:#4a7a4a; color:#1a5c1a; }
.badge-dot-SUCCESS  { background:#4a7a4a; }
.badge-PARTIAL      { background:#fffbe6; border-color:#b8920a; color:#7a5f00; }
.badge-dot-PARTIAL  { background:#b8920a; }
.badge-UNSUCCESSFUL { background:#fff0f0; border-color:#cc0000; color:#cc0000; }
.badge-dot-UNSUCCESSFUL { background:#cc0000; }
.badge-REBOOKED     { background:#f0f0ff; border-color:#4444aa; color:#2222aa; }
.badge-dot-REBOOKED { background:#4444aa; }
.badge-PROTECTED    { background:#e8f4ff; border-color:#1e6aab; color:#0d4a80; }
.badge-dot-PROTECTED { background:#1e6aab; }
.badge-UNABLE       { background:#fff0f0; border-color:#cc4444; color:#993333; }
.badge-dot-UNABLE   { background:#cc4444; }
.badge-MONITORING   { background:#fffbe6; border-color:#b8920a; color:#7a5f00; }
.badge-dot-MONITORING { background:#b8920a; }
.badge-CANCELLED    { background:#fff0f0; border-color:#cc0000; color:#cc0000; }
.badge-dot-CANCELLED { background:#cc0000; }

/* ── Section tag ──────────────────────────────────────── */
.section-tag        { font-size:8px; font-weight:600; letter-spacing:0.1em; text-transform:uppercase; color:#aaa; white-space:nowrap; }
.section-tag.active { color: var(--accent); }

/* ── Remark ───────────────────────────────────────────── */
.remark-row { background: var(--card); }
.remark-row td { padding: 6px 8px 10px; }
.remark-block { display: flex; flex-direction: column; gap: 3px; }
.remark-line  { word-wrap: break-word; overflow-wrap: break-word; }
.remark-tag   { font-size: 8px; font-weight: 700; color: var(--silver); text-transform: uppercase;
                letter-spacing: 0.1em; white-space: nowrap; margin-right: 6px; }
.remark-text  { font-size: 11px; color: var(--light); line-height: 1.5; }
.remark-entry { margin-bottom:8px; }
.remark-entry:last-child { margin-bottom:0; }
.remark-tag  { font-size:8px; font-weight:700; color:var(--silver); text-transform:uppercase;
               letter-spacing:0.1em; margin-bottom:2px; white-space:nowrap; }
.remark-text { font-size:11px; color:var(--light); line-height:1.5; }

/* ── Connection map ───────────────────────────────────── */
.map-section { margin-bottom: 24px; }
.map-title   { font-size: 8px; font-weight: 700; letter-spacing: 0.2em; color: var(--silver);
               text-transform: uppercase; margin-bottom: 8px; }
@media print { .map-section { page-break-inside: avoid; } }

/* ── Footer ───────────────────────────────────────────── */
.footer { margin-top:28px; padding-top:16px; border-top:1px solid var(--border);
          font-size:8px; color:var(--silver); letter-spacing:0.18em; text-transform:uppercase;
          text-align:center; display:flex; align-items:center; justify-content:center; gap:10px; }
.footer-star { color:var(--border); }

@media print {
  body { padding: 20px 24px; }
  @page { margin: 1.5cm; }
}
</style></head>
<body>

<div class="header">
  <div class="header-brand">
    <img src="${saLogoData || ""}" alt="Star Alliance" />
    <div>
      <div class="brand-title">Star Alliance</div>
      <div class="brand-sub">LAX Connect</div>
      <div class="brand-carrier">United Airlines</div>
    </div>
  </div>
  <div class="header-right">
    <div class="header-date">${h(dateLabel)}</div>
    <div class="header-meta">
      ${sccoLine ? `<div class="header-meta-row">Prepared by &nbsp;<span>${sccoLine}</span></div>` : ""}
      ${shiftDisplay ? `<div class="header-meta-row">Shift &nbsp;<span>${shiftDisplay}</span></div>` : ""}
      <div class="header-meta-row">Generated &nbsp;<span>${now}</span></div>
    </div>
  </div>
</div>

<div class="stats-bar">
  <!-- Header row -->
  <div class="stat-header-cell"></div>
  <div class="stat-header-cell"><div class="stat-label">Connections</div></div>
  <div class="stat-header-cell"><div class="stat-label">PAX at Risk</div></div>
  <div class="stat-header-cell"><div class="stat-label">Expedited</div></div>
  <div class="stat-header-cell"><div class="stat-label">Rebooked</div></div>
  <div class="stat-header-cell"><div class="stat-label">Bags at Risk</div></div>
  <div class="stat-header-cell"><div class="stat-label">Successful</div></div>
  <!-- Active shift row -->
  <div class="stat-cell-label"><div class="stat-row-label">Active<br>Shift</div></div>
  <div class="stat-cell"><div class="stat-val">${active.total}</div></div>
  <div class="stat-cell"><div class="stat-val">${active.pax}</div></div>
  <div class="stat-cell"><div class="stat-val">${active.exp}</div></div>
  <div class="stat-cell"><div class="stat-val">${active.rbk}</div></div>
  <div class="stat-cell"><div class="stat-val">${active.bags}</div></div>
  <div class="stat-cell"><div class="stat-val">${active.success}/${active.total}</div></div>
  ${outlookEntries.length > 0 ? `
  <!-- Post-shift row -->
  <div class="stat-cell-label"><div class="stat-row-label">Post-<br>Shift</div></div>
  <div class="stat-cell"><div class="stat-val">${outlook.total}</div></div>
  <div class="stat-cell"><div class="stat-val">${outlook.pax}</div></div>
  <div class="stat-cell"><div class="stat-val">${outlook.exp}</div></div>
  <div class="stat-cell"><div class="stat-val">${outlook.rbk}</div></div>
  <div class="stat-cell"><div class="stat-val">${outlook.bags}</div></div>
  <div class="stat-cell"><div class="stat-val">${outlook.success}/${outlook.total}</div></div>
  ` : ""}
</div>

<div class="map-section">
  <div class="map-title">Connection Map</div>
  ${buildMapSvg(sorted)}
</div>

<table>
  <thead><tr>
    <th>Inbound</th>
    <th>Outbound</th>
    <th class="center">Conn Time</th>
    <th class="center">PAX</th>
    <th class="center">Bags</th>
    <th>Status</th>
    <th class="center">Section</th>
  </tr></thead>
  <tbody>${rows}</tbody>
</table>

<div class="footer">
  <span>Star Alliance</span><span class="footer-star">✦</span>
  <span>LAX</span><span class="footer-star">✦</span>
  <span>United Airlines</span><span class="footer-star">✦</span>
  <span>${h(dateLabel)}</span>
</div>

</body></html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url  = URL.createObjectURL(blob);

  if (format === "pdf") {
    // Open in new tab and trigger the browser's native print-to-PDF dialog
    const win = window.open(url, "_blank", "noopener");
    if (win) {
      win.onload = () => {
        win.focus();
        win.print();
      };
    }
  } else {
    // Download as .html file
    const a    = document.createElement("a");
    a.href     = url;
    a.download = "LAX_Connection_Log_" + dateLabel.replace(/\s/g, "_") + ".html";
    a.click();
    URL.revokeObjectURL(url);
  }
}

export { exportCSV };
